<template>
  <MainScrollItem
    :item="result"
    tag="article"
    class="x-result x-group/result x-flex x-flex-col x-gap-4 x-rounded-md x-border-2 x-border-neutral-10"
    data-wysiwyg="result"
    :data-wysiwyg-id="result.id"
    :data-wysiwyg-image-url="result.images ? result.images[0] : ''"
    :data-wysiwyg-title="result.name"
  >
    <div class="x-relative">
      <BaseResultLink class="x-result__picture" :result="result">
        <BaseResultImage :result="result" :load-animation="imageAnimation" show-next-image-on-hover>
          <template #placeholder>
            <BasePlaceholderImage />
          </template>
          <template #fallback>
            <BaseFallbackImage />
          </template>
        </BaseResultImage>
      </BaseResultLink>

      <div
        v-if="discountLabel"
        class="x-absolute x-left-8 x-top-8 x-rounded x-px-8 x-py-4 x-text-xs x-font-bold x-uppercase x-text-neutral-0"
        :class="{
          'x-bg-lead-10': result.discountPercentage && result.discountPercentage < 15,
          'x-bg-lead-50':
            result.discountPercentage &&
            result.discountPercentage >= 15 &&
            result.discountPercentage < 30,
          'x-bg-lead-25': result.discountPercentage && result.discountPercentage >= 30,
        }"
      >
        {{ discountLabel }}
      </div>
    </div>

    <BaseResultLink
      class="x-result__description x-flex x-flex-col x-gap-2 x-p-16 desktop:x-gap-16"
      :result="result"
    >
      <h2
        class="x-title3 x-line-clamp-2 x-h-[50px] x-uppercase x-text-lead-75 desktop:x-h-[60px]"
        :class="isRecommendation ? 'desktop:x-text-[16px]' : 'desktop:x-text-[20px]'"
        data-test="result-title"
      >
        {{ result.name }}
      </h2>

      <div
        v-if="stockLabel"
        class="x-text-sm"
        :class="{
          'x-font-bold x-text-lead-10': result.stock && result.stock > 5,
          'x-font-semibold x-text-lead-90': result.stock && result.stock >= 1 && result.stock <= 5,
          'x-font-semibold x-text-lead-50': result.stock === 0,
        }"
      >
        {{ stockLabel }}
      </div>

      <div class="x-flex x-flex-wrap x-items-center x-gap-8">
        <BaseResultCurrentPrice
          :result="result"
          class="x-title2 x-text-[18px] x-font-bold x-text-lead-50"
        />
        <BaseResultPreviousPrice
          :result="result"
          class="x-title4 x-text-[13px] x-leading-[1.7] x-text-lead-75 x-line-through"
        />
      </div>

      <!-- Savings Label -->
      <div v-if="savingsAmount" class="x-text-sm x-text-lead-10">
        {{ t('result.savings') }} <BaseCurrency :value="savingsAmount" />
      </div>
    </BaseResultLink>
  </MainScrollItem>
</template>

<script setup lang="ts">
import type { Result } from '@empathyco/x-types'
import {
  BaseCurrency,
  BaseFallbackImage,
  BasePlaceholderImage,
  BaseResultCurrentPrice,
  BaseResultImage,
  BaseResultLink,
  BaseResultPreviousPrice,
  CrossFade,
} from '@empathyco/x-components'
import { MainScrollItem } from '@empathyco/x-components/scroll'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {
  result: Result
  showDescription?: boolean
  showAddToCart?: boolean
  isRecommendation?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showDescription: true,
  showAddToCart: true,
  isRecommendation: false,
})

const imageAnimation = CrossFade

const { t } = useI18n()

const discountLabel = computed(() => {
  const discount = props.result.discountPercentage
  if (!discount || discount <= 0) return null

  const percentage = `${Math.round(discount)}%`

  if (discount < 15) return `${t('result.discount')} -${percentage}`
  if (discount >= 15 && discount < 30) return `${t('result.offer')} -${percentage}`
  if (discount >= 30) return `${t('result.outlet')} -${percentage}`

  return null
})

const stockLabel = computed(() => {
  const stock = props.result.stock
  if (stock === undefined || stock === null) return null

  if (stock === 0) return t('result.outOfStock')
  if (stock > 5) return t('result.inStock')
  if (stock >= 1 && stock <= 5) return t('result.limitedStock')

  return null
})

const savingsAmount = computed(() => {
  const originalPrice = props.result.price?.originalValue
  const currentPrice = props.result.price?.value

  if (!originalPrice || !currentPrice || originalPrice <= currentPrice) return null

  return originalPrice - currentPrice
})
</script>
<style lang="scss">
.x-picture {
  aspect-ratio: 1/1 !important;
}
.x-result-picture-image {
  filter: brightness(97%) contrast(96%);
  overflow-anchor: none;
}
</style>
